/**
 * CategoryGridSlider
 * Custom element that adds prev/next scroll controls to a category grid slider.
 * Supports multiple instances on the same page.
 */
class CategoryGridSlider extends HTMLElement {
  /** @type {HTMLElement|null} */
  #track = null;

  /** @type {HTMLButtonElement|null} */
  #prev = null;

  /** @type {HTMLButtonElement|null} */
  #next = null;

  /** @type {ResizeObserver|null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#track = this.querySelector('.category-grid__cards');
    this.#prev = this.querySelector('[data-action="prev"]');
    this.#next = this.querySelector('[data-action="next"]');

    if (!this.#track) return;

    this.#prev?.addEventListener('click', this.#onPrev);
    this.#next?.addEventListener('click', this.#onNext);
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });

    // Re-evaluate button states when the track is resized
    // (e.g. viewport change, font load, theme editor resize)
    this.#resizeObserver = new ResizeObserver(this.#onResize);
    this.#resizeObserver.observe(this.#track);

    this.#updateButtons();
  }

  disconnectedCallback() {
    this.#prev?.removeEventListener('click', this.#onPrev);
    this.#next?.removeEventListener('click', this.#onNext);
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  // ── Private bound handlers ────────────────────────────────────────────────

  #onPrev = () => this.#scroll(-1);
  #onNext = () => this.#scroll(1);
  #onScroll = () => this.#updateButtons();
  #onResize = () => this.#updateButtons();

  // ── Core logic ────────────────────────────────────────────────────────────

  /**
   * Scroll the track by one card in the given direction.
   * @param {-1|1} dir - -1 for previous, 1 for next
   */
  #scroll(dir) {
    const card = this.#track.querySelector(':scope > .group-block');
    if (!card) return;

    const gap = parseFloat(getComputedStyle(this.#track).columnGap) || 0;
    const amount = card.offsetWidth + gap;

    this.#track.scrollBy({ left: dir * amount, behavior: 'smooth' });
  }

  /**
   * Enable/disable prev and next buttons based on current scroll position.
   * Uses a 1px tolerance to account for sub-pixel rendering differences.
   */
  #updateButtons() {
    if (!this.#track) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.#track;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

    if (this.#prev) this.#prev.disabled = atStart;
    if (this.#next) this.#next.disabled = atEnd;
  }

  // ── Shopify theme editor hooks ────────────────────────────────────────────

  onSectionLoad() {
    this.#updateButtons();
  }

  onSectionUnload() {
    this.disconnectedCallback();
  }
}

if (!customElements.get('category-grid-slider')) {
  customElements.define('category-grid-slider', CategoryGridSlider);
}
